// API configuration
// Set REACT_APP_API_URL in .env to your Railway URL
import { supabase } from './supabase';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function handleResponse(res) {
  if (!res.ok) throw new ApiError(res.status, res.statusText || `API error ${res.status}`);
  return res.json();
}

export async function scoresite(payload) {
  const res = await fetch(`${API_BASE}/score`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Spend a credit (or verified payment_id) to unlock full economics for a
// site area for 30 days; returns the full report and saves it to history.
export async function scoreUnlock(payload) {
  const res = await fetch(`${API_BASE}/score/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function downloadReportPdf(reportId) {
  const res = await fetch(`${API_BASE}/reports/${reportId}/pdf`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new ApiError(res.status, 'PDF generation failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `siteiq-report-${reportId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function getCategories() {
  const res = await fetch(`${API_BASE}/categories`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getCredits() {
  const res = await fetch(`${API_BASE}/credits`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getReports() {
  const res = await fetch(`${API_BASE}/reports`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getReport(reportId) {
  const res = await fetch(`${API_BASE}/reports/${reportId}`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getProducts() {
  const res = await fetch(`${API_BASE}/products`);
  return handleResponse(res);
}

export async function getAccount() {
  const res = await fetch(`${API_BASE}/account`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function createInvoice(payload) {
  const res = await fetch(`${API_BASE}/invoices/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function getInvoices() {
  const res = await fetch(`${API_BASE}/invoices`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getInvoice(invoiceId) {
  const res = await fetch(`${API_BASE}/invoices/${invoiceId}`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getInvoiceHtml(invoiceId) {
  const res = await fetch(`${API_BASE}/invoices/${invoiceId}/html`, {
    headers: await authHeaders(),
  });
  return res.text();
}

export async function getPoiPreview(lat, lon, category, radiusKm = 3) {
  const params = new URLSearchParams({ lat, lon, category, radius_km: radiusKm });
  const res = await fetch(`${API_BASE}/poi-preview?${params}`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getBrandLocations() {
  const res = await fetch(`${API_BASE}/brand/locations`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function getBrandStats() {
  const res = await fetch(`${API_BASE}/brand/stats`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function triggerBenchmark() {
  const res = await fetch(`${API_BASE}/brand/benchmark`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function refreshBrand() {
  const res = await fetch(`${API_BASE}/brand/refresh`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getNetworkRanking(brand, score, rep) {
  const params = new URLSearchParams({ brand, score, rep });
  const res = await fetch(`${API_BASE}/brand/ranking?${params}`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function searchCentres(q, province, tier, limit = 50) {
  const params = new URLSearchParams({ q });
  if (province) params.set('province', province);
  if (tier) params.set('tier', tier);
  params.set('limit', limit);
  const res = await fetch(`${API_BASE}/centres/search?${params}`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function getPortfolio() {
  const res = await fetch(`${API_BASE}/portfolio`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function addToPortfolio(centreIds) {
  const res = await fetch(`${API_BASE}/portfolio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ centre_ids: centreIds }),
  });
  return handleResponse(res);
}

export async function removeFromPortfolio(centreId) {
  const res = await fetch(`${API_BASE}/portfolio/${centreId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getPortfolioStats() {
  const res = await fetch(`${API_BASE}/portfolio/stats`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function triggerPortfolioBenchmark() {
  const res = await fetch(`${API_BASE}/portfolio/benchmark`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getSites(stage) {
  const params = stage ? `?stage=${stage}` : '';
  const res = await fetch(`${API_BASE}/sites${params}`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function createSite(payload) {
  const res = await fetch(`${API_BASE}/sites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function updateSite(siteId, payload) {
  const res = await fetch(`${API_BASE}/sites/${siteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function deleteSite(siteId) {
  const res = await fetch(`${API_BASE}/sites/${siteId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function getSiteStats() {
  const res = await fetch(`${API_BASE}/sites/stats`, { headers: await authHeaders() });
  return handleResponse(res);
}

// ---- Schema-agnostic intake ----
export async function analyzeIntake(csv, filename, kind) {
  const res = await fetch(`${API_BASE}/intake/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ csv, filename, kind }),
  });
  return handleResponse(res);
}

export async function commitIntake(csv, filename, mapping, options, kind) {
  const res = await fetch(`${API_BASE}/intake/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ csv, filename, mapping, options, kind }),
  });
  return handleResponse(res);
}

// ---- Predictive analytics: sales-model intake ----
export async function getModelStatus() {
  const res = await fetch(`${API_BASE}/brand/model/status`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function requestModel() {
  const res = await fetch(`${API_BASE}/brand/model/request`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function validateSales(csv) {
  const res = await fetch(`${API_BASE}/brand/sales/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ csv }),
  });
  return handleResponse(res);
}

export async function commitSales(csv, filename) {
  const res = await fetch(`${API_BASE}/brand/sales/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ csv, filename }),
  });
  return handleResponse(res);
}

// ---- Admin: brand verification ----
export async function getAdminEngagements() {
  const res = await fetch(`${API_BASE}/admin/engagements`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function verifyBrand(userId, verified) {
  const res = await fetch(`${API_BASE}/admin/verify-brand`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ user_id: userId, verified }),
  });
  return handleResponse(res);
}

export async function getBrandModel(category) {
  const res = await fetch(`${API_BASE}/brand/model?category=${encodeURIComponent(category)}`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function trainBrandModel(category) {
  const res = await fetch(`${API_BASE}/brand/model/train?category=${encodeURIComponent(category)}`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function predictSite(payload) {
  const res = await fetch(`${API_BASE}/brand/model/predict`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function getWhitespace(category) {
  const res = await fetch(`${API_BASE}/brand/whitespace?category=${encodeURIComponent(category)}`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function scanWhitespace(category) {
  const res = await fetch(`${API_BASE}/brand/whitespace/scan?category=${encodeURIComponent(category)}`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function scoreFeasibility(payload) {
  const res = await fetch(`${API_BASE}/score-feasibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function scoreFeasibilityUnlock(payload) {
  const res = await fetch(`${API_BASE}/score-feasibility/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function createPayment(payload) {
  const res = await fetch(`${API_BASE}/payments/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function getPaymentStatus(paymentId) {
  const res = await fetch(`${API_BASE}/payments/${paymentId}`, {
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export function submitPayfastForm(formAction, formFields) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = formAction;
  Object.entries(formFields).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

// Mock data for development (used when API is not connected)
export const MOCK_RESULT = {
  site_id: 'demo-site',
  category: 'qsr',
  total_score: 76,
  verdict: 'Proceed',
  verdict_color: 'green',
  confidence: 0.85,
  dominant_lsm: 'LSM9-10',
  city_tier: 'metro_primary',
  dimensions: [
    { name: 'Demand Density',  score: 88, weight: 0.15, label: 'Strong' },
    { name: 'Income Profile',  score: 82, weight: 0.20, label: 'Strong' },
    { name: 'Accessibility',   score: 74, weight: 0.15, label: 'Good' },
    { name: 'Visibility',      score: 70, weight: 0.10, label: 'Good' },
    { name: 'Competition',     score: 58, weight: 0.15, label: 'Moderate' },
    { name: 'Trade Area',      score: 80, weight: 0.15, label: 'Strong' },
    { name: 'Growth Signal',   score: null, weight: 0.0, label: 'N/A' },
  ],
  rep: {
    total:               81700000,
    gross:               96100000,
    source:              'subplace_spend',
    cannibalisation_pct: 15.0,
    own_brand_count:     1,
    lsm9_10:             53100000,
    lsm7_8:              18000000,
    lsm5_6:               6500000,
    lsm4_5:               2500000,
    lsm3_4:               1600000,
    hh_lsm9_10:          1940,
    hh_lsm7_8:           2330,
    hh_lsm5_6:           1550,
    hh_lsm4_5:            970,
    hh_lsm3_4:            977,
    total_hh:             7767,
  },
  revenue_scenarios: {
    conservative: 8170000,
    base:         12255000,
    optimistic:   16340000,
  },
  basket: {
    avg_basket: 219,
    monthly_transactions: 4660,
    visit_frequency: 4,
    active_hh: 1165,
    share_pct: 15,
  },
  saturation: {
    competitor_count: 5,
    hh_per_outlet: 1294,
    national_avg_hh_per_outlet: 3100,
    saturation_ratio: 2.39,
    verdict: 'Oversaturated',
    whitespace_outlets: 0,
    difficulty_factor: 2.72,
    difficulty_label: 'Very Hard',
  },
  visibility_detail: {
    visibility_score: 62.4,
    nearest_highway_m: 1200,
    nearest_primary_m: 350,
    nearest_secondary_m: 800,
    nearest_local_m: 45,
    road_counts: { highway: 3, primary: 8, secondary: 12, local: 45 },
  },
  drive_time: null,
  benchmark: {
    benchmark_count: 4,
    search_radius_km: 10,
    candidate_score: 76,
    candidate_rep: 81700000,
    score_rank: 2,
    rep_rank: 1,
    score_percentile: 80,
    rep_percentile: 100,
    vs_avg_score: 8.2,
    vs_avg_rep: 12500000,
    above_avg_score: true,
    above_avg_rep: true,
    sites: [
      { name: "McDonald's Fourways Mall", brand: "McDonald's",  dist_km: 0.8, score: 82, rep: 79000000, rating: 4.1, visibility: 72, accessibility: 78, rep_source: 'subplace_spend' },
      { name: 'Steers Fourways',          brand: 'Steers',      dist_km: 1.1, score: 68, rep: 65000000, rating: 3.7, visibility: 64, accessibility: 70, rep_source: 'subplace_spend' },
      { name: "Nando's Fourways",         brand: "Nando's",     dist_km: 1.8, score: 71, rep: 72000000, rating: 4.2, visibility: 58, accessibility: 74, rep_source: 'subplace_spend' },
      { name: 'Burger King Fourways',     brand: 'Burger King', dist_km: 2.3, score: 55, rep: 48000000, rating: 3.8, visibility: 48, accessibility: 62, rep_source: 'suburb_profile' },
    ],
  },
  competitors: [
    { name: "McDonald's Fourways Mall", brand: "McDonald's", category: 'QSR', dist_km: 0.8, rating: 4.1, threat: 'High',      threat_pct: 92 },
    { name: 'KFC Fourways Gardens',     brand: 'KFC',         category: 'QSR', dist_km: 1.4, rating: 3.9, threat: 'Own Brand', threat_pct: 0 },
    { name: 'Steers Fourways',          brand: 'Steers',      category: 'QSR', dist_km: 1.1, rating: 3.7, threat: 'Medium',    threat_pct: 89 },
    { name: "Nando's Fourways",         brand: "Nando's",     category: 'QSR', dist_km: 1.8, rating: 4.2, threat: 'Medium',    threat_pct: 82 },
    { name: 'Burger King Fourways',     brand: 'Burger King', category: 'QSR', dist_km: 2.3, rating: 3.8, threat: 'Low',       threat_pct: 77 },
  ],
};

export const MOCK_SC_RESULT = {
  site_id: 'demo-sc',
  category: 'shopping_centre',
  total_score: 72,
  verdict: 'Proceed',
  verdict_color: 'green',
  confidence: 0.78,
  dominant_lsm: 'LSM7-8',
  city_tier: 'metro_secondary',
  dimensions: [
    { name: 'Demand Density',  score: 82, weight: 0.15, label: 'Strong' },
    { name: 'Income Profile',  score: 74, weight: 0.20, label: 'Good' },
    { name: 'Accessibility',   score: 68, weight: 0.15, label: 'Good' },
    { name: 'Visibility',      score: 76, weight: 0.10, label: 'Good' },
    { name: 'Competition',     score: 62, weight: 0.15, label: 'Moderate' },
    { name: 'Trade Area',      score: 78, weight: 0.15, label: 'Good' },
    { name: 'Growth Signal',   score: null, weight: 0.0, label: 'N/A' },
  ],
  rep: {
    total: 248000000, gross: 248000000, source: 'subplace_spend',
    cannibalisation_pct: 0, own_brand_count: 0,
    lsm9_10: 72000000, lsm7_8: 88000000, lsm5_6: 52000000, lsm4_5: 24000000, lsm3_4: 12000000,
    hh_lsm9_10: 2800, hh_lsm7_8: 4200, hh_lsm5_6: 3100, hh_lsm4_5: 1800, hh_lsm3_4: 1400,
    total_hh: 13300,
  },
  feasibility: {
    recommended_gla_sqm: 18500,
    centre_type: 'community',
    supportable_spend: 186000000,
    tenant_mix: [
      { category: 'Food / Grocery',     pct: 30, gla_sqm: 5550, trading_density: 4165 },
      { category: 'Apparel',            pct: 18, gla_sqm: 3330, trading_density: 1621 },
      { category: 'Food Service / QSR', pct: 15, gla_sqm: 2775, trading_density: 2793 },
      { category: 'Department Stores',  pct: 12, gla_sqm: 2220, trading_density: 2895 },
      { category: 'Home Decor',         pct: 5,  gla_sqm: 925,  trading_density: 1194 },
      { category: 'Services / Other',   pct: 20, gla_sqm: 3700, trading_density: 1800 },
    ],
    competing_centres: [
      { name: 'Existing Centre A', dist_km: 3.2, gla_sqm: 22000, type: 'community' },
      { name: 'Existing Centre B', dist_km: 5.8, gla_sqm: 45000, type: 'regional' },
    ],
    anchor_recommendation: 'Shoprite or Pick n Pay (value grocery anchor aligned with LSM 7-8 dominant catchment)',
  },
  revenue_scenarios: { conservative: 24800000, base: 37200000, optimistic: 49600000 },
  basket: null,
  saturation: {
    competitor_count: 12, hh_per_outlet: 1025, national_avg_hh_per_outlet: 3100,
    saturation_ratio: 1.85, verdict: 'Oversaturated', whitespace_outlets: 0,
    difficulty_factor: 2.1, difficulty_label: 'Hard',
  },
  visibility_detail: { visibility_score: 71, nearest_highway_m: 800, nearest_primary_m: 120, nearest_secondary_m: 450, nearest_local_m: 30 },
  drive_time: null,
  benchmark: null,
  competitors: [],
};

export const CATEGORIES = [
  { key: 'qsr',             label: 'Quick Service Restaurant', icon: '🍔', examples: 'KFC, McDonald\'s, Steers' },
  { key: 'fashion_value',   label: 'Fashion Value',            icon: '👕', examples: 'Pep, Ackermans, Jet' },
  { key: 'fashion_mid',     label: 'Fashion Mid Market',       icon: '👗', examples: 'Woolworths, Edgars, Foschini' },
  { key: 'grocery_value',   label: 'Grocery Value',            icon: '🛒', examples: 'Shoprite, Boxer, Usave' },
  { key: 'grocery_premium', label: 'Grocery Premium',          icon: '🧺', examples: 'Woolworths Food, Pick n Pay' },
  { key: 'pharmacy',        label: 'Pharmacy',                 icon: '💊', examples: 'Dischem, Clicks' },
  { key: 'coffee',          label: 'Coffee & Café',            icon: '☕', examples: 'Starbucks, Vida, Seattle' },
  { key: 'casual_dining',   label: 'Casual Dining',            icon: '🍽️', examples: 'Spur, Wimpy, Ocean Basket' },
  { key: 'fuel',            label: 'Fuel & Convenience',       icon: '⛽', examples: 'Engen, BP, Shell' },
];
